import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  Download, 
  User, 
  Clock, 
  CheckCircle, 
  TrendingUp, 
  AlertTriangle,
  BarChart3,
  Activity,
  Target,
  Calendar,
  FileText,
  MessageSquare,
  ArrowUpRight,
  ArrowRight,
  XCircle,
  PauseCircle,
  Loader2,
  RefreshCw,
  AlertCircle,
  Info,
  Award,
  Zap,
  Users,
  Star,
  TrendingDown
} from 'lucide-react';
import toast from 'react-hot-toast';
import StaffReportService, { ValidationError, StaffReportError } from '../../services/StaffReportService';

const StaffReportDetail = ({ staff, dateRange, onClose }) => {
  const queryClient = useQueryClient();
  
  // State management
  const [exportFormat, setExportFormat] = useState('pdf');
  const [includeDetails, setIncludeDetails] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [localDateRange, setLocalDateRange] = useState(dateRange);
  const [validationErrors, setValidationErrors] = useState({});
  const [activeInsightTab, setActiveInsightTab] = useState('overview');
  
  // Validate date range in real-time
  const dateValidation = useMemo(() => {
    const errors = {};
    
    if (localDateRange.date_from && localDateRange.date_to) {
      const fromDate = new Date(localDateRange.date_from);
      const toDate = new Date(localDateRange.date_to);
      
      if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
        errors.dateFormat = 'Invalid date format';
      } else {
        if (fromDate > toDate) {
          errors.dateRange = 'From date must be before to date';
        }
        
        if (toDate > new Date()) {
          errors.dateTo = 'To date cannot be in the future';
        }

        const daysDiff = (toDate - fromDate) / (1000 * 60 * 60 * 24);
        if (daysDiff > 365) {
          errors.dateRange = 'Date range cannot exceed 365 days';
        }
      }
    }
    
    return { isValid: Object.keys(errors).length === 0, errors };
  }, [localDateRange]);

  // Update validation errors when date range changes
  useEffect(() => {
    setValidationErrors(dateValidation.errors || {});
  }, [dateValidation]);

  // Get detailed staff report with enhanced error handling
  const { 
    data: reportData, 
    isLoading, 
    error, 
    refetch,
    isFetching 
  } = useQuery({
    queryKey: ['staff-report-detail', staff.id, localDateRange, includeDetails],
    queryFn: async () => {
      try {
        const response = await StaffReportService.getStaffReport(staff.id, {
          ...localDateRange,
          include_details: includeDetails
        });
        return response;
      } catch (error) {
        if (error instanceof ValidationError) {
          throw new Error(`Invalid parameters: ${Object.values(error.context?.errors || {}).join(', ')}`);
        }
        if (error instanceof StaffReportError) {
          throw new Error(`Report error: ${error.message}`);
        }
        throw error;
      }
    },
    enabled: !!staff.id && dateValidation.isValid,
    retry: (failureCount, error) => {
      // Don't retry validation errors
      if (error.message.includes('Invalid parameters') || error.message.includes('Report error:')) {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 60000, // Cache for 1 minute
    gcTime: 300000 // Keep in cache for 5 minutes
  });

  // Format report data safely
  const report = useMemo(() => {
    if (!reportData?.data) return null;
    try {
      return StaffReportService.StaffReportFormatter?.formatReportData ? 
        StaffReportService.StaffReportFormatter.formatReportData(reportData) : 
        reportData.data;
    } catch (error) {
      console.error('Error formatting report data:', error);
      return reportData.data;
    }
  }, [reportData]);

  // Generate insights and performance indicators
  const insights = useMemo(() => {
    if (!report || !StaffReportService.generateInsights) return [];
    try {
      return StaffReportService.generateInsights(report);
    } catch (error) {
      console.error('Error generating insights:', error);
      return [];
    }
  }, [report]);

  const performanceIndicators = useMemo(() => {
    if (!report || !StaffReportService.calculatePerformanceIndicators) return {};
    try {
      return StaffReportService.calculatePerformanceIndicators(report.performanceMetrics);
    } catch (error) {
      console.error('Error calculating performance indicators:', error);
      return {};
    }
  }, [report]);

  // Handle export with enhanced error handling
  const handleExport = async () => {
    if (!dateValidation.isValid) {
      toast.error('Please fix date range errors before exporting');
      return;
    }

    setIsExporting(true);
    try {
      const exportParams = {
        ...localDateRange,
        format: exportFormat,
        include_details: includeDetails
      };

      // Remove empty values
      Object.keys(exportParams).forEach(key => {
        if (exportParams[key] === '' || exportParams[key] === null || exportParams[key] === undefined) {
          delete exportParams[key];
        }
      });

      await StaffReportService.downloadStaffReport(staff.id, exportParams);
      toast.success(`Report exported as ${exportFormat.toUpperCase()}`);
    } catch (error) {
      console.error('Export failed:', error);
      
      if (error instanceof ValidationError) {
        toast.error(`Export failed: ${Object.values(error.context?.errors || {}).join(', ')}`);
      } else if (error instanceof StaffReportError) {
        toast.error(`Export failed: ${error.message}`);
      } else {
        toast.error('Failed to export report. Please try again.');
      }
    } finally {
      setIsExporting(false);
    }
  };

  // Handle date range change with validation
  const handleDateRangeChange = (field, value) => {
    setLocalDateRange(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation errors for this field
    if (validationErrors[field] || validationErrors.dateRange) {
      setValidationErrors(prev => {
        const { [field]: removed, dateRange, ...rest } = prev;
        return rest;
      });
    }
  };

  // Apply date range changes with validation
  const handleApplyDateRange = () => {
    if (!dateValidation.isValid) {
      toast.error('Please fix date range errors first');
      return;
    }
    
    // Invalidate and refetch the query
    queryClient.invalidateQueries(['staff-report-detail', staff.id]);
    toast.success('Date range updated');
  };

  // Reset date range to original
  const handleResetDateRange = () => {
    setLocalDateRange(dateRange);
    setValidationErrors({});
  };

  // Quick date range presets
  const handleDatePreset = (preset) => {
    const now = new Date();
    let fromDate, toDate;

    switch (preset) {
      case 'last7':
        fromDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        toDate = now;
        break;
      case 'last30':
        fromDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        toDate = now;
        break;
      case 'last90':
        fromDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        toDate = now;
        break;
      case 'thisMonth':
        fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
        toDate = now;
        break;
      default:
        return;
    }

    setLocalDateRange({
      date_from: fromDate.toISOString().split('T')[0],
      date_to: toDate.toISOString().split('T')[0]
    });
    setValidationErrors({});
  };

  // Get performance indicator styles
  const getIndicatorStyles = (indicator) => {
    if (!indicator) return { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' };
    
    const styles = {
      excellent: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', icon: Star },
      good: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300', icon: CheckCircle },
      average: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', icon: AlertTriangle },
      poor: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', icon: TrendingDown }
    };
    
    return styles[indicator.status] || styles.average;
  };

  // Get status display with proper icons and colors
  const getStatusDisplay = (status, count) => {
    const displays = {
      open: { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Open' },
      assigned: { icon: User, color: 'text-yellow-600', bg: 'bg-yellow-50', label: 'Assigned' },
      in_progress: { icon: Activity, color: 'text-orange-600', bg: 'bg-orange-50', label: 'In Progress' },
      pending_customer: { icon: PauseCircle, color: 'text-gray-600', bg: 'bg-gray-50', label: 'Pending Customer' },
      escalated: { icon: ArrowUpRight, color: 'text-red-600', bg: 'bg-red-50', label: 'Escalated' },
      resolved: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', label: 'Resolved' },
      closed: { icon: XCircle, color: 'text-gray-800', bg: 'bg-gray-100', label: 'Closed' }
    };

    const display = displays[status] || { icon: FileText, color: 'text-gray-600', bg: 'bg-gray-50', label: status };
    const IconComponent = display.icon;

    return (
      <div className={`${display.bg} rounded-lg p-4 text-center transition-all hover:shadow-sm`}>
        <div className={`${display.color} mb-2`}>
          <IconComponent className="w-6 h-6 mx-auto" />
        </div>
        <div className={`text-2xl font-bold ${display.color} mb-1`}>{count || 0}</div>
        <div className="text-sm text-gray-600">{display.label}</div>
      </div>
    );
  };

  // Format numbers safely
  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '0';
    return new Intl.NumberFormat().format(num);
  };

  // Format percentage safely
  const formatPercentage = (value, decimals = 1) => {
    if (value === null || value === undefined || isNaN(value)) return '0.0%';
    return `${Number(value).toFixed(decimals)}%`;
  };

  // Format duration using service formatter
  const formatDuration = (minutes) => {
    if (StaffReportService.StaffReportFormatter?.formatDuration) {
      return StaffReportService.StaffReportFormatter.formatDuration(minutes);
    }
    
    if (!minutes || minutes === 0) return '0 min';
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.round(minutes % 60);
    
    if (hours === 0) return `${remainingMinutes} min`;
    if (remainingMinutes === 0) return `${hours}h`;
    return `${hours}h ${remainingMinutes}m`;
  };

  // Get available export formats
  const exportFormats = StaffReportService.getAvailableFormats ? 
    StaffReportService.getAvailableFormats() : 
    [
      { value: 'pdf', label: 'PDF Document', icon: '📄' },
      { value: 'csv', label: 'CSV Spreadsheet', icon: '📊' }
    ];

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading staff report...</p>
          <p className="text-sm text-gray-500 mt-2">Generating detailed performance analysis</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Report</h3>
          <p className="text-gray-600 mb-4">{error.message}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => refetch()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
            <button
              onClick={onClose}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!report) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No report data available</p>
          <button
            onClick={onClose}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enhanced Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <button
              onClick={onClose}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <User className="w-6 h-6 mr-2" />
                {report.staffInfo.name} - Performance Report
              </h1>
              <p className="text-gray-600 mt-1 flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                {report.reportPeriod.from} to {report.reportPeriod.to} ({report.reportPeriod.days} days)
                {report.reportPeriod.category_filter && (
                  <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    Category: {report.reportPeriod.category_filter}
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Refresh Button */}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100 disabled:opacity-50"
            title="Refresh Report"
          >
            <RefreshCw className={`w-5 h-5 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Date Range Controls */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">From:</label>
              <input
                type="date"
                value={localDateRange.date_from || ''}
                onChange={(e) => handleDateRangeChange('date_from', e.target.value)}
                className={`px-3 py-1 border rounded text-sm ${
                  validationErrors.date_from || validationErrors.dateRange ? 
                  'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">To:</label>
              <input
                type="date"
                value={localDateRange.date_to || ''}
                onChange={(e) => handleDateRangeChange('date_to', e.target.value)}
                className={`px-3 py-1 border rounded text-sm ${
                  validationErrors.date_to || validationErrors.dateRange ? 
                  'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleApplyDateRange}
                disabled={!dateValidation.isValid || isFetching}
                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                Apply
              </button>
              
              <button
                onClick={handleResetDateRange}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200 transition-colors"
              >
                Reset
              </button>
            </div>

            {/* Quick Presets */}
            <div className="flex gap-1">
              {[
                { key: 'last7', label: '7D' },
                { key: 'last30', label: '30D' },
                { key: 'last90', label: '90D' },
                { key: 'thisMonth', label: 'Month' }
              ].map((preset) => (
                <button
                  key={preset.key}
                  onClick={() => handleDatePreset(preset.key)}
                  className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Export Controls */}
            <div className="ml-auto flex items-center gap-3">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Include Details:</label>
                <input
                  type="checkbox"
                  checked={includeDetails}
                  onChange={(e) => setIncludeDetails(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </div>
              
              <select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {exportFormats.map((format) => (
                  <option key={format.value} value={format.value}>
                    {format.label}
                  </option>
                ))}
              </select>
              
              <button
                onClick={handleExport}
                disabled={isExporting || !dateValidation.isValid}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {isExporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Export Report
              </button>
            </div>
          </div>

          {/* Validation Errors */}
          {Object.keys(validationErrors).length > 0 && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded p-3">
              <div className="flex items-center">
                <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                <span className="text-sm font-medium text-red-800">Date Range Errors:</span>
              </div>
              <ul className="mt-1 text-sm text-red-700 list-disc list-inside">
                {Object.entries(validationErrors).map(([field, error]) => (
                  <li key={field}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Staff Information Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Basic Info */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <User className="w-5 h-5 mr-2" />
              Staff Information
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Name:</span>
                <span className="font-medium">{report.staffInfo.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Email:</span>
                <span className="font-medium text-sm">{report.staffInfo.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">ID Number:</span>
                <span className="font-medium">{report.staffInfo.user_id_number || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Staff Level:</span>
                <span className="font-medium">{report.staffInfo.staff_level_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  report.staffInfo.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {report.staffInfo.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              {report.staffInfo.last_login_at && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Login:</span>
                  <span className="text-sm text-gray-500">
                    {new Date(report.staffInfo.last_login_at).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Expertise Areas */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Award className="w-5 h-5 mr-2" />
              Expertise Areas
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {report.staffInfo.expertise && report.staffInfo.expertise.length > 0 ? (
                report.staffInfo.expertise.map((expertise, index) => (
                  <div key={index} className="bg-blue-50 px-3 py-2 rounded-lg">
                    <div className="font-medium text-blue-900 text-sm">{expertise.category_name}</div>
                    <div className="text-xs text-blue-700 flex items-center gap-2 mt-1">
                      <span>Level: {expertise.expertise_level || 'Standard'}</span>
                      <span>•</span>
                      <span>Max: {expertise.max_concurrent_tickets} tickets</span>
                      {expertise.can_receive_forwarded && (
                        <>
                          <span>•</span>
                          <span className="text-green-700">Accepts Forwards</span>
                        </>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-sm">No expertise areas defined</p>
              )}
            </div>
            
            {/* Expertise Summary */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="text-center p-2 bg-gray-50 rounded">
                  <div className="font-bold text-gray-900">{report.staffInfo.expertiseSummary.total_categories}</div>
                  <div className="text-gray-600">Total Categories</div>
                </div>
                <div className="text-center p-2 bg-green-50 rounded">
                  <div className="font-bold text-green-900">{report.staffInfo.expertiseSummary.can_receive_forwarded}</div>
                  <div className="text-green-600">Forward Ready</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Report Metadata */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Info className="w-5 h-5 mr-2" />
              Report Details
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Generated:</span>
                <span className="text-sm">
                  {report.reportMetadata?.generated_at ? 
                    new Date(report.reportMetadata.generated_at).toLocaleDateString() :
                    new Date().toLocaleDateString()
                  }
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Generated By:</span>
                <span className="text-sm">
                  {report.reportMetadata?.generated_by?.user_name || 'System'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Period:</span>
                <span className="text-sm font-medium">
                  {report?.reportPeriod?.days != null ? Math.floor(Number(report.reportPeriod.days)) : 0} days
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Details Included:</span>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  includeDetails ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {includeDetails ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Insights */}
      {insights.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
            Performance Insights
          </h3>
          
          {/* Insight Tabs */}
          <div className="flex gap-2 mb-4">
            {['overview', 'positive', 'attention'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveInsightTab(tab)}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  activeInsightTab === tab 
                    ? 'bg-blue-100 text-blue-800 font-medium' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab === 'overview' ? 'All' : tab === 'positive' ? 'Strengths' : 'Areas for Improvement'}
                <span className="ml-1 text-xs">
                  ({insights.filter(insight => 
                    tab === 'overview' ? true : 
                    tab === 'positive' ? ['positive', 'info'].includes(insight.type) : 
                    ['warning', 'attention'].includes(insight.type)
                  ).length})
                </span>
              </button>
            ))}
          </div>

          <div className="space-y-3">
            {insights
              .filter(insight => 
                activeInsightTab === 'overview' ? true : 
                activeInsightTab === 'positive' ? ['positive', 'info'].includes(insight.type) : 
                ['warning', 'attention'].includes(insight.type)
              )
              .map((insight, index) => {
                const bgColor = {
                  positive: 'bg-green-50 border-green-200',
                  warning: 'bg-yellow-50 border-yellow-200',
                  attention: 'bg-red-50 border-red-200',
                  info: 'bg-blue-50 border-blue-200'
                }[insight.type] || 'bg-gray-50 border-gray-200';

                const textColor = {
                  positive: 'text-green-800',
                  warning: 'text-yellow-800',
                  attention: 'text-red-800',
                  info: 'text-blue-800'
                }[insight.type] || 'text-gray-800';

                const iconColor = {
                  positive: 'text-green-600',
                  warning: 'text-yellow-600',
                  attention: 'text-red-600',
                  info: 'text-blue-600'
                }[insight.type] || 'text-gray-600';

                const IconComponent = {
                  positive: CheckCircle,
                  warning: AlertTriangle,
                  attention: XCircle,
                  info: Info
                }[insight.type] || Info;

                return (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border ${bgColor} transition-all hover:shadow-sm`}
                  >
                    <div className="flex items-start">
                      <IconComponent className={`w-5 h-5 ${iconColor} mr-3 mt-0.5 flex-shrink-0`} />
                      <div className="flex-1">
                        <h4 className={`font-medium ${textColor} mb-1`}>
                          {insight.title}
                        </h4>
                        <p className={`text-sm ${textColor.replace('800', '700')} leading-relaxed`}>
                          {insight.message}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            
            {insights.filter(insight => 
              activeInsightTab === 'overview' ? true : 
              activeInsightTab === 'positive' ? ['positive', 'info'].includes(insight.type) : 
              ['warning', 'attention'].includes(insight.type)
            ).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Info className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>No insights available for this category.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Performance Metrics Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Response Time */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-blue-600" />
              Response Time
            </h3>
            <Zap className="w-5 h-5 text-blue-600" />
          </div>
          
          <div className="space-y-4">
            <div className="text-3xl font-bold text-gray-900">
              {formatDuration(report.performanceMetrics.response_metrics?.average_response_time_minutes)}
            </div>
            
            {performanceIndicators.responseTime && (() => {
              const styles = getIndicatorStyles(performanceIndicators.responseTime);
              const IconComponent = styles.icon;
              return (
                <div className={`inline-flex items-center px-3 py-1 text-sm rounded-full border ${
                  styles.bg
                } ${styles.text} ${styles.border}`}>
                  {IconComponent && <IconComponent className="w-4 h-4 mr-1" />}
                  {performanceIndicators.responseTime.label}
                </div>
              );
            })()}
            
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>Fastest:</span>
                <span className="font-medium">
                  {formatDuration(report.performanceMetrics.response_metrics?.fastest_response_minutes)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Slowest:</span>
                <span className="font-medium">
                  {formatDuration(report.performanceMetrics.response_metrics?.slowest_response_minutes)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Total Responses:</span>
                <span className="font-medium">
                  {formatNumber(report.performanceMetrics.response_metrics?.total_responses)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* SLA Compliance */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900 flex items-center">
              <Target className="w-5 h-5 mr-2 text-green-600" />
              SLA Compliance
            </h3>
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          
          <div className="space-y-4">
            <div className="text-3xl font-bold text-gray-900">
              {formatPercentage(report.performanceMetrics.sla_performance?.sla_compliance_rate)}
            </div>
            
            {performanceIndicators.slaCompliance && (() => {
              const styles = getIndicatorStyles(performanceIndicators.slaCompliance);
              const IconComponent = styles.icon;
              return (
                <div className={`inline-flex items-center px-3 py-1 text-sm rounded-full border ${
                  styles.bg
                } ${styles.text} ${styles.border}`}>
                  {IconComponent && <IconComponent className="w-4 h-4 mr-1" />}
                  {performanceIndicators.slaCompliance.label}
                </div>
              );
            })()}
            
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>On Time:</span>
                <span className="font-medium text-green-600">
                  {formatNumber(report.performanceMetrics.sla_performance?.on_time_responses)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>With Deadlines:</span>
                <span className="font-medium">
                  {formatNumber(report.performanceMetrics.sla_performance?.total_with_deadlines)}
                </span>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full transition-all" 
                style={{ 
                  width: `${Math.min(report.performanceMetrics.sla_performance?.sla_compliance_rate || 0, 100)}%` 
                }}
              ></div>
            </div>
          </div>
        </div>

        {/* Completion Rate */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2 text-purple-600" />
              Completion Rate
            </h3>
            <Activity className="w-5 h-5 text-purple-600" />
          </div>
          
          <div className="space-y-4">
            <div className="text-3xl font-bold text-gray-900">
              {formatPercentage(report.performanceMetrics.workload_metrics?.completion_rate)}
            </div>
            
            {performanceIndicators.completionRate && (() => {
              const styles = getIndicatorStyles(performanceIndicators.completionRate);
              const IconComponent = styles.icon;
              return (
                <div className={`inline-flex items-center px-3 py-1 text-sm rounded-full border ${
                  styles.bg
                } ${styles.text} ${styles.border}`}>
                  {IconComponent && <IconComponent className="w-4 h-4 mr-1" />}
                  {performanceIndicators.completionRate.label}
                </div>
              );
            })()}
            
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>Completed:</span>
                <span className="font-medium text-green-600">
                  {formatNumber(report.performanceMetrics.workload_metrics?.completed_assignments)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Total Assignments:</span>
                <span className="font-medium">
                  {formatNumber(report.performanceMetrics.workload_metrics?.total_assignments)}
                </span>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-purple-500 h-2 rounded-full transition-all" 
                style={{ 
                  width: `${Math.min(report.performanceMetrics.workload_metrics?.completion_rate || 0, 100)}%` 
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Ticket Statistics */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
          <FileText className="w-5 h-5 mr-2 text-blue-600" />
          Ticket Lifecycle Overview
        </h3>
        
        {/* Status Distribution Grid */}
        {/* <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          {Object.entries(report.ticketStatistics.by_status || {}).map(([status, count]) => (
            <div key={status}>
              {getStatusDisplay(status, count)}
            </div>
          ))}
        </div> */}

        {/* Summary Statistics */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {formatNumber(report.ticketStatistics.total_assigned)}
            </div>
            <div className="text-sm text-gray-600">Total Assigned</div>
          </div>
          
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-900 mb-1">
              {formatNumber(
                (report.ticketStatistics.by_status?.resolved || 0) + 
                (report.ticketStatistics.by_status?.closed || 0)
              )}
            </div>
            <div className="text-sm text-green-600">Completed</div>
          </div>
          
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-900 mb-1">
              {formatPercentage(report.ticketStatistics.completion_metrics?.completion_rate)}
            </div>
            <div className="text-sm text-blue-600">Completion Rate</div>
          </div>
          
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-900 mb-1">
              {formatNumber(report.ticketStatistics.completion_metrics?.average_resolution_time_hours)}h
            </div>
            <div className="text-sm text-purple-600">Avg Resolution Time</div>
          </div>
        </div>

        {/* Actions Performed */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h4 className="font-medium text-gray-900 mb-4 flex items-center">
            <Activity className="w-5 h-5 mr-2 text-gray-600" />
            Actions Performed
          </h4>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center">
                <MessageSquare className="w-4 h-4 text-blue-600 mr-2" />
                <span className="text-sm text-blue-900">Comments</span>
              </div>
              <span className="font-bold text-blue-900">
                {formatNumber(report.actionStatistics?.comments_added)}
              </span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                <span className="text-sm text-green-900">Resolved</span>
              </div>
              <span className="font-bold text-green-900">
                {formatNumber(report.actionStatistics?.tickets_resolved)}
              </span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <XCircle className="w-4 h-4 text-gray-600 mr-2" />
                <span className="text-sm text-gray-900">Closed</span>
              </div>
              <span className="font-bold text-gray-900">
                {formatNumber(report.actionStatistics?.tickets_closed)}
              </span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
              <div className="flex items-center">
                <ArrowUpRight className="w-4 h-4 text-orange-600 mr-2" />
                <span className="text-sm text-orange-900">Escalated</span>
              </div>
              <span className="font-bold text-orange-900">
                {formatNumber(report.actionStatistics?.escalated)}
              </span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
              <div className="flex items-center">
                <ArrowRight className="w-4 h-4 text-purple-600 mr-2" />
                <span className="text-sm text-purple-900">Forwarded</span>
              </div>
              <span className="font-bold text-purple-900">
                {formatNumber(report.actionStatistics?.forwarded)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Category Performance */}
      {report.categoryBreakdown && report.categoryBreakdown.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
            <BarChart3 className="w-5 h-5 mr-2 text-purple-600" />
            Category Performance Breakdown
          </h3>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resolved
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Closed
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Escalated
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Success Rate
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {report.categoryBreakdown.map((category) => {
                  const completed = (category.resolved_tickets || 0) + (category.closed_tickets || 0);
                  const successRate = category.total_tickets > 0 ? 
                    (completed / category.total_tickets) * 100 : 0;
                  
                  return (
                    <tr key={category.category_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {category.category_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className="text-sm font-medium text-gray-900">
                          {formatNumber(category.total_tickets)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className="text-sm font-medium text-green-600">
                          {formatNumber(category.resolved_tickets)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className="text-sm font-medium text-gray-800">
                          {formatNumber(category.closed_tickets)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className="text-sm font-medium text-orange-600">
                          {formatNumber(category.escalated_tickets)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center">
                          <div className="flex items-center min-w-0 flex-1 max-w-32">
                            <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                              <div 
                                className={`h-2 rounded-full transition-all ${
                                  successRate >= 80 ? 'bg-green-500' :
                                  successRate >= 60 ? 'bg-yellow-500' :
                                  'bg-red-500'
                                }`}
                                style={{ width: `${Math.min(successRate, 100)}%` }}
                              ></div>
                            </div>
                            <span className="text-xs font-medium text-gray-900 whitespace-nowrap">
                              {successRate.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Category Summary */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="text-center">
                <div className="font-bold text-lg text-gray-900">
                  {report.categoryBreakdown.length}
                </div>
                <div className="text-gray-600">Categories Worked</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-lg text-green-900">
                  {report.categoryBreakdown.filter(cat => 
                    (cat.total_tickets > 0 && ((cat.resolved_tickets + cat.closed_tickets) / cat.total_tickets) >= 0.8)
                  ).length}
                </div>
                <div className="text-green-600">High Success Rate (≥80%)</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-lg text-blue-900">
                  {Math.round(
                    report.categoryBreakdown.reduce((sum, cat) => sum + (cat.total_tickets || 0), 0) / 
                    Math.max(report.categoryBreakdown.length, 1)
                  )}
                </div>
                <div className="text-blue-600">Avg Tickets per Category</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Statistics */}
      {report.assignmentStatistics && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
            <Users className="w-5 h-5 mr-2 text-indigo-600" />
            Assignment & Workload Analysis
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-4 border border-indigo-200 rounded-lg">
              <div className="text-2xl font-bold text-indigo-900 mb-1">
                {formatNumber(report.assignmentStatistics.total_assignments)}
              </div>
              <div className="text-sm text-indigo-600">Total Assignments</div>
            </div>
            
            <div className="text-center p-4 border border-green-200 rounded-lg">
              <div className="text-2xl font-bold text-green-900 mb-1">
                {formatNumber(report.assignmentStatistics.completed_assignments)}
              </div>
              <div className="text-sm text-green-600">Completed</div>
            </div>
            
            <div className="text-center p-4 border border-orange-200 rounded-lg">
              <div className="text-2xl font-bold text-orange-900 mb-1">
                {formatNumber(report.assignmentStatistics.in_progress_assignments)}
              </div>
              <div className="text-sm text-orange-600">In Progress</div>
            </div>
            
            <div className="text-center p-4 border border-gray-200 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 mb-1">
                {formatNumber(report.assignmentStatistics.pending_assignments)}
              </div>
              <div className="text-sm text-gray-600">Pending</div>
            </div>
          </div>
          
          {/* Assignment Actions */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Assignment Actions</h4>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <span className="text-sm text-red-900">Escalated</span>
                <span className="font-bold text-red-900">
                  {formatNumber(report.assignmentStatistics.escalated_assignments)}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <span className="text-sm text-purple-900">Forwarded</span>
                <span className="font-bold text-purple-900">
                  {formatNumber(report.assignmentStatistics.forwarded_assignments)}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-cyan-50 rounded-lg">
                <span className="text-sm text-cyan-900">Returned</span>
                <span className="font-bold text-cyan-900">
                  {formatNumber(report.assignmentStatistics.returned_assignments)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffReportDetail;